import { useState, useMemo } from "react";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Clock, Expand, Search, Filter, ListFilter, Calendar as CalendarIcon, 
  ChevronDown, User, Hospital, CheckCircle2, XCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";

interface ScheduleSetup {
  id: string;
  dentistName: string;
  clinicName: string;
  periodStart: Date;
  periodEnd: Date;
  active: boolean;
  daysOfWeek: string[];
}

const MOCK_DATA: ScheduleSetup[] = [
  {
    id: "1",
    dentistName: "Dr. Ricardo Oliveira",
    clinicName: "Unidade Nova Iguaçu",
    periodStart: new Date(2024, 0, 1),
    periodEnd: new Date(2024, 11, 31),
    active: true,
    daysOfWeek: ["Seg", "Ter", "Qua"],
  },
  {
    id: "2",
    dentistName: "Dra. Ana Costa",
    clinicName: "Unidade Copacabana",
    periodStart: new Date(2024, 1, 1),
    periodEnd: new Date(2024, 5, 30),
    active: false,
    daysOfWeek: ["Qui", "Sex"],
  },
  {
    id: "3",
    dentistName: "Dr. Paulo Souza",
    clinicName: "Unidade Nova Iguaçu",
    periodStart: new Date(2024, 0, 15),
    periodEnd: new Date(2024, 6, 15),
    active: true,
    daysOfWeek: ["Seg", "Qua", "Sex"],
  },
  {
    id: "4",
    dentistName: "Dra. Beatriz Santos",
    clinicName: "Unidade Copacabana",
    periodStart: new Date(2024, 2, 1),
    periodEnd: new Date(2024, 8, 30),
    active: true,
    daysOfWeek: ["Ter", "Qui"],
  }
];

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"];

export default function ScheduleSetupPage() {
  const { toast } = useToast();
  const [schedules, setSchedules] = useState<ScheduleSetup[]>(MOCK_DATA);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDentist, setSelectedDentist] = useState("all");
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [selectedDay, setSelectedDay] = useState("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const [onlyActive, setOnlyActive] = useState(true);
  const [pageSize, setPageSize] = useState("10");

  const toggleStatus = (id: string) => {
    setSchedules(prev => prev.map(s => {
      if (s.id === id) {
        const newStatus = !s.active;
        toast({
          title: newStatus ? "Expediente Ativado" : "Expediente Inativado",
          description: `O expediente do ${s.dentistName} foi ${newStatus ? 'ativado' : 'inativado'}.`,
        });
        return { ...s, active: newStatus };
      }
      return s;
    }));
  };

  const expandPeriod = (id: string) => {
    setSchedules(prev => prev.map(s => {
      if (s.id === id) {
        const newEnd = new Date(s.periodEnd);
        newEnd.setMonth(newEnd.getMonth() + 6);
        toast({
          title: "Período Expandido",
          description: `O período do ${s.dentistName} foi expandido até ${format(newEnd, "dd/MM/yyyy")}.`,
        });
        return { ...s, periodEnd: newEnd };
      }
      return s;
    }));
  };

  const filteredSchedules = useMemo(() => {
    return schedules.filter(s => {
      const matchesSearch = s.dentistName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           s.clinicName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDentist = selectedDentist === "all" || s.dentistName === selectedDentist;
      const matchesClinic = selectedClinic === "all" || s.clinicName === selectedClinic;
      const matchesDay = selectedDay === "all" || s.daysOfWeek.includes(selectedDay);
      const matchesActive = !onlyActive || s.active;
      
      // Basic date overlap check if dateRange is set
      let matchesDate = true;
      if (dateRange?.from && dateRange?.to) {
        matchesDate = (s.periodStart <= dateRange.to && s.periodEnd >= dateRange.from);
      }

      return matchesSearch && matchesDentist && matchesClinic && matchesDay && matchesActive && matchesDate;
    });
  }, [schedules, searchTerm, selectedDentist, selectedClinic, selectedDay, onlyActive, dateRange]);

  return (
    <div className="page-wrapper p-6 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Configuração de Expedientes (Schedule Setup)</h1>
        </div>
        <Badge variant="outline" className="text-sm px-3 py-1">
          {filteredSchedules.length} registro(s) encontrado(s)
        </Badge>
      </div>

      {/* Filter Area */}
      <Card className="border-primary/20 shadow-sm">
        <CardHeader className="py-3 px-5 border-b bg-muted/30">
          <div className="flex items-center gap-2 text-primary">
            <Filter className="h-4 w-4" />
            <CardTitle className="text-sm font-semibold uppercase tracking-wider">Filtros de Busca</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Parte do nome / Busca Geral</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Nome do profissional ou clínica..." 
                  className="pl-9 h-9 text-sm"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Profissional</Label>
              <Select value={selectedDentist} onValueChange={setSelectedDentist}>
                <SelectTrigger className="h-9 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue placeholder="Todos os profissionais" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os profissionais</SelectItem>
                  {Array.from(new Set(MOCK_DATA.map(s => s.dentistName))).map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Clínica</Label>
              <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                <SelectTrigger className="h-9 text-sm">
                  <div className="flex items-center gap-2">
                    <Hospital className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue placeholder="Todas as clínicas" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as clínicas</SelectItem>
                  {Array.from(new Set(MOCK_DATA.map(s => s.clinicName))).map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Dia da Semana</Label>
              <Select value={selectedDay} onValueChange={setSelectedDay}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Qualquer dia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer dia</SelectItem>
                  {DAYS.map(day => (
                    <SelectItem key={day} value={day}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Período de Vigência</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full h-9 justify-start text-left font-normal text-sm",
                      !dateRange && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd/MM/yy")} - {format(dateRange.to, "dd/MM/yy")}
                        </>
                      ) : (
                        format(dateRange.from, "dd/MM/yy")
                      )
                    ) : (
                      <span>Selecionar período</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Registros por Página</Label>
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger className="h-9 text-sm">
                  <div className="flex items-center gap-2">
                    <ListFilter className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 registros</SelectItem>
                  <SelectItem value="25">25 registros</SelectItem>
                  <SelectItem value="50">50 registros</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4 pt-6">
              <div className="flex items-center gap-2">
                <Switch 
                  id="active-only" 
                  checked={onlyActive} 
                  onCheckedChange={setOnlyActive}
                />
                <Label htmlFor="active-only" className="text-xs font-semibold cursor-pointer">Somente ativos</Label>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-muted-foreground h-8"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedDentist("all");
                  setSelectedClinic("all");
                  setSelectedDay("all");
                  setDateRange(undefined);
                  setOnlyActive(true);
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid Area */}
      <Card className="flex-1 min-h-0 flex flex-col overflow-hidden shadow-sm">
        <CardHeader className="py-3 px-5 border-b bg-muted/10 flex-shrink-0">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Listagem de Expedientes</CardTitle>
        </CardHeader>
        <CardContent className="p-0 flex-1 overflow-auto">
          <Table>
            <TableHeader className="bg-muted/50 sticky top-0 z-10">
              <TableRow>
                <TableHead className="w-[200px] text-xs font-bold">Profissional</TableHead>
                <TableHead className="w-[180px] text-xs font-bold">Clínica</TableHead>
                <TableHead className="text-xs font-bold">Dias de Atendimento</TableHead>
                <TableHead className="w-[200px] text-xs font-bold">Período de Vigência</TableHead>
                <TableHead className="w-[120px] text-center text-xs font-bold">Status</TableHead>
                <TableHead className="w-[150px] text-right text-xs font-bold">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSchedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                    Nenhum expediente encontrado para os filtros selecionados.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSchedules.map((schedule) => (
                  <TableRow key={schedule.id} className={cn("transition-colors hover:bg-muted/30", !schedule.active && "bg-muted/10 opacity-70")}>
                    <TableCell className="font-semibold text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                          {schedule.dentistName.split(" ").pop()?.[0]}
                        </div>
                        {schedule.dentistName}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Hospital className="h-3 w-3" />
                        {schedule.clinicName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {DAYS.map(day => (
                          <Badge 
                            key={day} 
                            variant={schedule.daysOfWeek.includes(day) ? "default" : "outline"} 
                            className={cn(
                              "text-[9px] px-1.5 py-0 uppercase tracking-tighter",
                              schedule.daysOfWeek.includes(day) ? "bg-primary/80" : "text-muted-foreground/50 border-muted-foreground/20"
                            )}
                          >
                            {day}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      <div className="flex items-center gap-1.5">
                        <CalendarIcon className="h-3 w-3 text-muted-foreground" />
                        {format(schedule.periodStart, "dd/MM/yyyy")} a {format(schedule.periodEnd, "dd/MM/yyyy")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-center gap-1.5">
                        <div className="flex items-center gap-2">
                          {schedule.active ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                          )}
                          <Switch 
                            checked={schedule.active} 
                            onCheckedChange={() => toggleStatus(schedule.id)}
                            className="scale-75"
                          />
                        </div>
                        <span className={cn(
                          "text-[10px] font-bold uppercase",
                          schedule.active ? "text-green-600" : "text-muted-foreground"
                        )}>
                          {schedule.active ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => expandPeriod(schedule.id)}
                        className="gap-1.5 h-8 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-all"
                      >
                        <Expand className="h-3.5 w-3.5" />
                        Expandir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
